import React, { lazy, Suspense, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'
import { FullPageSpinner } from 'src/components/Reusable/FullPageSpinner'
import { GatekeeperFlag, IsGatekeeperEnabled } from 'src/context/Gatekeeper'
import 'bootstrap/dist/css/bootstrap.min.css'
import 'react-toastify/dist/ReactToastify.css'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { useAuth } from '../context/authContext'
import { AppUnauth } from './AppUnauth'
import { usePublicTrustPortalAccount } from './App.hooks'

type ErrorProps = {
  error: Error
}

const ErrorFallback = (props: ErrorProps): JSX.Element => {
  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre>{props.error.message}</pre>
    </div>
  )
}

export const App = (): JSX.Element => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ProductChooser />
    </ErrorBoundary>
  )
}

const ProductChooser = () => {
  const isTrustPortalEnabled = IsGatekeeperEnabled(GatekeeperFlag.TRUST_PORTAL)
  const { trustAccountId } = usePublicTrustPortalAccount()
  const isVendorPortalRoute = useIsVendorPortalRoute()

  // Checked ahead of the Trust Portal / main-app split below: the vendor
  // portal is its own standalone, nav-less app (see VendorPortalApp.tsx),
  // identified purely by URL path rather than by resolving an account
  // up front like Trust Portal does -- account/session resolution for the
  // vendor portal happens inside VendorPortalApp itself (M1.4's routing
  // decision), not here.
  if (isVendorPortalRoute) {
    return <VendorPortal />
  }

  if (isTrustPortalEnabled) {
    // Tri-state logic:
    // 1. Loading - fetching trust account id
    // 2. No account id - redirect to main app, we are not in the trust portal
    // 3. Account id - trust account id found, render trust portal
    if (trustAccountId === undefined) {
      return <FullPageSpinner />
    }

    if (trustAccountId) {
      return <TrustPortal />
    }
  }

  return <AppChooser />
}

const TrustPortal = () => {
  const TrustApp = useMemo(() => lazy(() => import('./TrustApp')), [])

  return (
    <Suspense fallback={<></>}>
      <TrustApp />
    </Suspense>
  )
}

// VENDOR_PORTAL_ROUTE_PATTERN matches the vendor-portal's account-scoped URL
// shape, /<accountId>/vendor-portal(/...) (epic AP-19372's URL restructure --
// see VendorPortalQuestionnaire.routes.tsx for the frontend path builders
// this must stay in sync with), as well as the legacy pre-restructure bare
// /vendor-portal shape with no account segment at all -- old invite/resend
// emails already sent before this change link to that bare path, and this
// app still needs to mount VendorPortalApp for it so its landing flow
// (VendorPortalApp.hooks.tsx) can exchange the token and rewrite the URL to
// the new account-scoped shape once the account resolves, rather than the
// link simply dead-ending. Kept as a plain path-prefix check (unlike Trust
// Portal's domain/account-id resolution in usePublicTrustPortalAccount)
// since vendor-portal routing doesn't need to resolve an account before
// deciding to mount the app -- that happens inside VendorPortalApp itself,
// off the accountId segment this pattern matches but doesn't otherwise use.
const VENDOR_PORTAL_ROUTE_PATTERN = /^\/(?:[^/]+\/)?vendor-portal(\/|$)/

const useIsVendorPortalRoute = (): boolean => {
  const { pathname } = useLocation()
  return VENDOR_PORTAL_ROUTE_PATTERN.test(pathname)
}

const VendorPortal = () => {
  const VendorPortalApp = useMemo(
    () => lazy(() => import('./VendorPortalApp')),
    [],
  )

  return (
    <Suspense fallback={<></>}>
      <VendorPortalApp />
    </Suspense>
  )
}

const AppChooser = () => {
  const AppAuth = useMemo(() => lazy(() => import('./AppAuth')), [])

  const { accountId, isAuthenticated } = useAuth()

  if (!isAuthenticated || !accountId) {
    return <AppUnauth />
  }

  return (
    <Suspense fallback={<></>}>
      <AppAuth accountId={accountId} />
    </Suspense>
  )
}
